import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { currentOwnerId } from '../auth.js';
import { db } from '../db/local.js';
import { completeTask, datedTasks } from '../db/tasks.js';
import type { DueTask } from '../db/tasks.js';
import { useToday } from '../hooks/useToday.js';

/**
 * Aufgaben auf „Heute": was heute fällig oder überfällig ist und mich betrifft.
 * Nichts fällig — keine Kachel. Abhaken geht direkt hier.
 */
export function TodayTasks({
  highlightId,
  onOpenTasks,
}: {
  highlightId: string | null;
  onOpenTasks: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const today = useToday();
  const me = currentOwnerId();
  const due = useLiveQuery(
    () => (today ? datedTasks({ me, today, to: today }, db) : Promise.resolve([] as DueTask[])),
    [today, me],
    [] as DueTask[],
  );
  if (!today || due.length === 0) return null;

  return (
    <section aria-labelledby="today-tasks" className="kw-tile flex flex-col gap-1">
      <h2 id="today-tasks" className="kw-kicker">
        {t('tasks.todayHeading')}
      </h2>
      <ul className="flex flex-col">
        {due.map((task) => (
          <li
            key={task.id}
            className={`flex min-h-11 items-center gap-2 rounded-lg ${
              task.id === highlightId ? 'border border-accent' : ''
            }`}
          >
            <label className="flex size-11 shrink-0 items-center justify-center">
              <input
                type="checkbox"
                checked={false}
                onChange={() => void completeTask(task.id, { me, today }, db)}
                aria-label={t('tasks.completeNamed', { title: task.title })}
                className="size-6 accent-[var(--color-accent)]"
              />
            </label>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm">{task.title}</span>
              <span
                className={`block truncate text-xs ${task.state === 'overdue' ? 'text-warn' : 'text-ink-muted'}`}
              >
                {task.state === 'overdue' ? t('tasks.overdue') : t('tasks.today')}
                {task.dueTime ? ` · ${task.dueTime}` : ''} · {task.listName}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <button type="button" onClick={onOpenTasks} className="kw-btn kw-btn-quiet kw-btn-small">
        {t('tasks.allTasks')}
      </button>
    </section>
  );
}
